import { useState } from "react";

interface PortalHeaderProps {
  moduleName?: string;
  variant?: "light" | "dark";
  onSignOut?: () => void;
  showNavigation?: boolean;
  // Extended props for future use
  currentPage?: string;
  userName?: string;
  unitNumber?: string;
  buildingName?: string;
}

export default function PortalHeader({
  moduleName = "Atrium Resident Portal",
  variant = "light",
  onSignOut,
  showNavigation = true,
  // Extended props (not used in current App.tsx flow but kept for future use)
  currentPage,
  userName,
}: PortalHeaderProps) {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const isDark = variant === "dark";

  return (
    <header
      style={{
        height: "88px",
        background: isDark ? "#09111D" : "var(--white)",
        borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "var(--line)"}`,
        display: "flex",
        alignItems: "center",
        padding: "0 32px",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      {/* Left: Logo and Brand */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
      }}>
        {/* Crastu Logo */}
        <a
          href="https://crastu.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <img
            src="/logo/crastu.png"
            alt="Crastu"
            width={44}
            height={44}
            style={{ display: "block" }}
          />
        </a>

        {/* ATRIUM by Crastu */}
        <div
          style={{
            display: "none",
            flexDirection: "column",
            gap: "2px",
          }}
          className="desktop-only"
        >
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "20px",
              fontWeight: 600,
              letterSpacing: "0.16em",
              color: isDark ? "var(--white)" : "var(--ink)",
            }}
          >
            ATRIUM CMD
          </div>
          <div
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: isDark ? "var(--sand)" : "var(--stone)",
            }}
          >
            by Crastu
          </div>
        </div>
      </div>

      {/* Center: Module Name */}
      {moduleName && (
        <div
          className="module-name"
          style={{
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            fontWeight: 500,
            color: isDark ? "var(--sand)" : "var(--stone)",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {moduleName}
        </div>
      )}

      {/* Right: Navigation and Actions */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
      }}>
        {/* Navigation - Desktop */}
        {showNavigation && (
          <nav
            className="desktop-only"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              marginRight: "16px",
            }}
          >
            <a
              href="https://crastu.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: isDark ? "var(--sand)" : "var(--stone)",
                textDecoration: "none",
                padding: "8px 12px",
                borderRadius: "6px",
                transition: "all 0.2s",
              }}
            >
              Crastu
            </a>
            <a
              href="https://ops.atriumla.online/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: isDark ? "var(--sand)" : "var(--stone)",
                textDecoration: "none",
                padding: "8px 12px",
                borderRadius: "6px",
                transition: "all 0.2s",
              }}
            >
              Command Center
            </a>
          </nav>
        )}

        {/* Sign Out Button */}
        {onSignOut && (
          <button
            onClick={onSignOut}
            className="btn-ghost"
            style={{
              padding: "10px 18px",
              background: isDark ? "rgba(255, 255, 255, 0.08)" : "var(--azzurro-50)",
              color: isDark ? "var(--sand)" : "var(--azzurro-700)",
              border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.16)" : "var(--azzurro-200)"}`,
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            Sign Out
          </button>
        )}

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-only"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          style={{
            padding: "8px",
            background: "transparent",
            border: "none",
            marginLeft: "8px",
          }}
        >
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none">
            {showMobileMenu ? (
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={isDark ? "var(--white)" : "var(--ink)"}
                strokeWidth={2}
                strokeLinecap="round"
              />
            ) : (
              <path
                d="M3 12h18M3 6h18M3 18h18"
                stroke={isDark ? "var(--white)" : "var(--ink)"}
                strokeWidth={2}
                strokeLinecap="round"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {showMobileMenu && (
        <div
          style={{
            position: "fixed",
            top: "88px",
            left: 0,
            right: 0,
            background: isDark ? "#09111D" : "var(--white)",
            padding: "20px",
            boxShadow: "var(--shadow-soft)",
            zIndex: 999,
            borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "var(--line)"}`,
          }}
          className="mobile-only"
        >
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}>
            {showNavigation && (
              <>
                <a
                  href="https://crastu.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "block",
                    padding: "12px 16px",
                    textDecoration: "none",
                    color: isDark ? "var(--sand)" : "var(--stone)",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  Crastu
                </a>
                <a
                  href="https://ops.atriumla.online/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "block",
                    padding: "12px 16px",
                    textDecoration: "none",
                    color: isDark ? "var(--sand)" : "var(--stone)",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  Command Center
                </a>
              </>
            )}
            {onSignOut && (
              <button
                onClick={onSignOut}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "12px 16px",
                  background: isDark ? "rgba(255, 255, 255, 0.08)" : "var(--azzurro-50)",
                  color: isDark ? "var(--sand)" : "var(--azzurro-700)",
                  border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.16)" : "var(--azzurro-200)"}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  textAlign: "left",
                  marginTop: "8px",
                }}
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

/* Add inline styles for responsive classes */
const style = document.createElement('style');
style.textContent = `
  @media (min-width: 769px) {
    .desktop-only { display: flex !important; }
    .mobile-only { display: none !important; }
  }
  @media (max-width: 768px) {
    .desktop-only { display: none !important; }
    .mobile-only { display: flex !important; }
  }
`;
document.head.appendChild(style);
